import { fetchContactsRequest } from '@/services/api/contact';
import { fetchCustomerWithLeadsRequest } from '@/services/api/customer';
import { processAPIResponseNow } from '@/utils/request';
import { ContactCustomerLink, ContactModel } from '@/typings/models/Contact';
import { LeadModel } from '@/typings/models/Lead';
import { extractContactList, findDuplicateContact, getContactHomeCustomer } from '@/utils/contacts';

/** `GET /contacts` ignores shorter terms and would list everyone. */
const MIN_TERM_LENGTH = 3;

export type ExistingContactCustomer = ContactCustomerLink & {
  phone?: string | null;
  locale?: string | null;
  gender?: string | null;
  leads?: LeadModel[];
};

export type ExistingContact = { contact: ContactModel; customer: ExistingContactCustomer };

const read = <T>(response: any, pick: (body: any) => T, fallback: T) =>
  new Promise<T>((resolve) =>
    processAPIResponseNow(response, { onSuccess: (body) => resolve(pick(body)), onError: () => resolve(fallback) }),
  );

const findContact = async (email?: string, phone?: string) => {
  for (const term of [email, phone].map((value) => (value || '').trim())) {
    if (term.length < MIN_TERM_LENGTH) continue;
    const response = await fetchContactsRequest({ query: { search: term, page_size: 10, skip_total_size: 1 } });
    const contacts = await read(response, (body) => extractContactList(body).items, [] as ContactModel[]);
    // The search matches by prefix; only the exact email or phone counts.
    const match = findDuplicateContact(contacts, { email, phone });
    if (match) return match.contact;
  }
  return undefined;
};

/**
 * The partner's contact with this email (else phone) and the customer a new
 * project for them goes to, with that customer's projects when they load.
 */
export const findExistingContact = async (email?: string, phone?: string): Promise<ExistingContact | undefined> => {
  const contact = await findContact(email, phone);
  const link = getContactHomeCustomer(contact);
  if (!contact || !link) return undefined;
  const response = await fetchCustomerWithLeadsRequest(link.id);
  const customers = await read(response, (body) => (Array.isArray(body?.data) ? body.data : []), [] as any[]);
  const customer = customers.find((item) => item.id === link.id);
  return { contact, customer: customer ? { ...link, ...customer } : link };
};
